import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import UpdateNotification from "./components/UpdateNotification";
import { registerServiceWorker } from "./services/pushNotifications";

// Pages
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import HousekeeperDashboard from "./pages/HousekeeperDashboard";
import HousekeeperProfile from "./pages/HousekeeperProfile";
import TaskDetail from "./pages/TaskDetail";
import Planning from "./pages/Planning";
import UsersManagement from "./pages/UsersManagement";
import PropertiesManagement from "./pages/PropertiesManagement";
import Payments from "./pages/Payments";
import SuspectPhotos from "./pages/SuspectPhotos";
import Signalements from "./pages/Signalements";
import AdminFdmProfile from "./pages/AdminFdmProfile";
import HousekeeperTasks from "./pages/HousekeeperTasks";
import HousekeeperEarnings from "./pages/HousekeeperEarnings";
import GouvernantDashboard from "./pages/GouvernantDashboard";
import GouvernantPlanning from "./pages/GouvernantPlanning";
import GouvernantMap from "./pages/GouvernantMap";
import PropertyTracking from "./pages/PropertyTracking";
import PriceRates from "./pages/PriceRates";
import PhotoGallery from "./pages/PhotoGallery";
import PublicGallery from "./pages/PublicGallery";
import CleaningTimeStats from "./pages/CleaningTimeStats";
import ContractsManagement from "./pages/ContractsManagement";
import ContractSign from "./pages/ContractSign";
import DepositsManagement from "./pages/DepositsManagement";
import DepositPay from "./pages/DepositPay";
import GeoWhitelist from "./pages/GeoWhitelist";

// Register Service Worker on app load — but NOT on public deposit page
// Voyageurs don't need offline mode, and SW caching breaks Stripe Payment Element
if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/caution/')) {
  registerServiceWorker();
}

// Loading component with MCB logo
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="text-center">
      <img 
        src="/logo-mcb-footer.png" 
        alt="MA CONCIERGERIE BRETONNE" 
        className="h-16 mx-auto animate-pulse"
      />
      <p className="mt-4 text-slate-500 text-sm">Chargement...</p>
    </div>
  </div>
);

// Root redirect based on role
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'technicien') {
    return <Navigate to="/tech" replace />;
  } else if (user.role === 'gouvernant') {
    return <Navigate to="/gouvernant" replace />;
  } else {
    return <Navigate to="/app" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/gallery/:shareToken" element={<PublicGallery />} />
          <Route path="/contracts/sign/:token" element={<ContractSign />} />
          <Route path="/caution/:token" element={<DepositPay />} />
          <Route path="/admin/geo-whitelist" element={<GeoWhitelist />} />
          
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Admin Routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planning" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'technicien']}>
                <Planning />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/tasks/:id" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'technicien']}>
                <TaskDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UsersManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/properties" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PropertiesManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/payments" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Payments />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/photos-suspectes" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'technician']}>
                <SuspectPhotos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/signalements" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'technicien']}>
                <Signalements />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/users/:userId/profil" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminFdmProfile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/suivi-logements" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PropertyTracking />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/tarifs" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PriceRates />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/stats-temps" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CleaningTimeStats />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/contrats" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ContractsManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/cautions" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DepositsManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/galerie" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PhotoGallery />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/surveillance" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SuspectPhotos />
              </ProtectedRoute>
            } 
          />
          {/* Redirect old routes to new unified page */}
          <Route 
            path="/admin/photos-suspectes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SuspectPhotos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/fdm-suspectes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SuspectPhotos />
              </ProtectedRoute>
            } 
          />

          {/* Technician Routes */}
          <Route 
            path="/tech" 
            element={
              <ProtectedRoute allowedRoles={['technicien']}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Gouvernant Routes */}
          <Route 
            path="/gouvernant" 
            element={
              <ProtectedRoute allowedRoles={['gouvernant']}>
                <GouvernantDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gouvernant/planning" 
            element={
              <ProtectedRoute allowedRoles={['gouvernant']}>
                <GouvernantPlanning />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gouvernant/carte" 
            element={
              <ProtectedRoute allowedRoles={['gouvernant']}>
                <GouvernantMap />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gouvernant/suivi-logements" 
            element={
              <ProtectedRoute allowedRoles={['gouvernant']}>
                <PropertyTracking />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gouvernant/galerie" 
            element={
              <ProtectedRoute allowedRoles={['gouvernant']}>
                <PhotoGallery />
              </ProtectedRoute>
            } 
          />

          {/* Housekeeper Routes */}
          <Route 
            path="/app" 
            element={
              <ProtectedRoute allowedRoles={['housekeeper']}>
                <HousekeeperDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/app/tasks" 
            element={
              <ProtectedRoute allowedRoles={['housekeeper']}>
                <HousekeeperTasks />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/app/task/:id" 
            element={
              <ProtectedRoute allowedRoles={['housekeeper']}>
                <TaskDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/app/earnings" 
            element={
              <ProtectedRoute allowedRoles={['housekeeper']}>
                <HousekeeperEarnings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/app/profile" 
            element={
              <ProtectedRoute allowedRoles={['housekeeper', 'technician']}>
                <HousekeeperProfile />
              </ProtectedRoute>
            } 
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      <UpdateNotification />
    </AuthProvider>
  );
}

export default App;
